import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import StructuredData from '../../components/base/StructuredData';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const EmpresaPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);

    document.title = 'Empresa de Vaciado de Pisos en Mollet del Vallès | JUSTCLEAN';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Conoce JUSTCLEAN, empresa especializada en vaciado de pisos y limpieza Diógenes en Mollet del Vallès. Más de 10 años de experiencia y +500 pisos vaciados.'
      );
    }
  }, []);

  const valores = [
    {
      icon: 'ri-shield-check-line',
      title: 'Profesionalidad',
      description: 'Equipo cualificado con más de 10 años de experiencia en vaciado de pisos y limpieza extrema',
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Discreción',
      description: 'Máxima confidencialidad en todos nuestros servicios, especialmente en casos de síndrome de Diógenes',
    },
    {
      icon: 'ri-time-line',
      title: 'Rapidez',
      description: 'Servicio urgente disponible 24 horas. Presupuesto en menos de 24h y ejecución inmediata',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Responsabilidad',
      description: 'Gestión responsable de residuos con reciclaje y donación de enseres en buen estado',
    },
  ];

  const metodoTrabajo = [
    {
      title: 'Evaluación Inicial Personalizada',
      description: 'Visitamos el espacio sin compromiso para entender tus necesidades específicas y ofrecerte la mejor solución adaptada a tu situación en Mollet del Vallès o comarca.',
      icon: 'ri-search-eye-line',
    },
    {
      title: 'Planificación Detallada',
      description: 'Diseñamos un plan de trabajo optimizado que incluye tiempos, recursos necesarios y coordinación con comunidades de vecinos si es necesario.',
      icon: 'ri-calendar-check-line',
    },
    {
      title: 'Ejecución Profesional',
      description: 'Nuestro equipo ejecuta el vaciado con máxima eficiencia, utilizando equipamiento profesional y siguiendo protocolos de seguridad y limpieza.',
      icon: 'ri-team-line',
    },
    {
      title: 'Garantía de Satisfacción',
      description: 'Revisamos contigo el resultado final y no damos por terminado el trabajo hasta que estés completamente satisfecho con el servicio prestado.',
      icon: 'ri-medal-line',
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'JUSTCLEAN Mollet del Vallès',
      description: 'Empresa especializada en vaciado de pisos, locales y limpieza por síndrome de Diógenes en Mollet del Vallès y Vallès Oriental con más de 10 años de experiencia',
      foundingDate: '2014',
      telephone: '+34602365996',
      email: 'info@mollet.justclean.es',
      areaServed: 'Mollet del Vallès, Vallès Oriental, Barcelona',
    },
  };

  return (
    <div className={`min-h-screen transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <StructuredData data={structuredData} />
      <Header />

      <main>
        <section
          className="relative h-96 flex items-center justify-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/2d28ecc9696da2749ed18819c5643873.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-dark/60"></div>
          <div className="relative z-10 text-center px-4">
            <nav className="text-white/70 text-sm mb-4">
              <Link to="/" className="hover:text-white">
                Inicio
              </Link>
              <span className="mx-2">&gt;</span>
              <span className="text-white">Empresa</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Empresa de vaciado y limpieza profesional en Mollet
            </h1>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  Sobre JUSTCLEAN
                </div>

                <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6 leading-tight">
                  Más de 10 años especializados en vaciado de pisos
                </h2>

                <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                  <p>
                    <strong>JUSTCLEAN</strong> nace en 2014 en Mollet del Vallès con una misión clara: ofrecer servicios profesionales de vaciado de pisos, vaciado de locales y limpieza por síndrome de Diógenes con la máxima calidad, discreción y respeto.
                  </p>

                  <p>
                    Durante más de una década, hemos ayudado a cientos de familias, administradores de fincas, inmobiliarias y particulares en Mollet del Vallès, Parets del Vallès, Montmeló, Granollers y toda la comarca del Vallès Oriental a resolver situaciones complejas relacionadas con el vaciado de viviendas y la limpieza extrema.
                  </p>

                  <p>
                    Nos especializamos en casos de <strong>síndrome de Diógenes y suciedad extrema</strong>, donde la sensibilidad, la experiencia y el trato humano son fundamentales. Entendemos que cada situación es única y requiere un enfoque personalizado y discreto.
                  </p>

                  <p>
                    Trabajamos estrechamente con servicios sociales, administradores de fincas, inmobiliarias y comunidades de vecinos en Mollet del Vallès y alrededores, ofreciendo soluciones integrales que van desde el vaciado completo hasta la retirada de muebles, limpieza profunda, pintura y pequeñas reparaciones.
                  </p>
                </div>

                <div className="mt-8 space-y-3">
                  <div className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-primary text-2xl"></i>
                    <span className="text-dark font-medium">Más de 500 pisos vaciados en el Vallès Oriental</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-primary text-2xl"></i>
                    <span className="text-dark font-medium">Especialistas en síndrome de Diógenes</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-primary text-2xl"></i>
                    <span className="text-dark font-medium">Servicio urgente 24 horas disponible</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-primary text-2xl"></i>
                    <span className="text-dark font-medium">Gestión responsable de residuos y reciclaje</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20cleaning%20service%20team%20portrait%20friendly%20workers%20in%20uniform%20standing%20together%20confident%20reliable%20trustworthy%20appearance%20modern%20clean%20background%20diverse%20group%20smiling%20professional%20service%20industry&width=800&height=1000&seq=team-justclean&orientation=portrait"
                    alt="Equipo JUSTCLEAN Mollet del Vallès"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>

                <div className="absolute -bottom-6 -right-6 bg-primary text-white p-8 rounded-xl shadow-xl">
                  <div className="text-5xl font-extrabold">+500</div>
                  <div className="text-lg font-medium mt-2">Pisos Vaciados</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-secondary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-4">Nuestro método de trabajo</h2>
              <div className="w-16 h-1 bg-primary mx-auto"></div>
              <p className="mt-6 text-lg text-gray-700 max-w-3xl mx-auto">
                Un proceso probado y perfeccionado durante años de experiencia en vaciado de pisos y limpieza extrema en Mollet del Vallès y comarca del Vallès Oriental.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {metodoTrabajo.map((metodo, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-md relative">
                  <div className="absolute top-8 right-8 text-8xl font-extrabold text-primary/5">{index + 1}</div>

                  <div className="relative z-10">
                    <div className="w-16 h-16 flex items-center justify-center mb-6">
                      <i className={`${metodo.icon} text-5xl text-primary`}></i>
                    </div>

                    <h3 className="text-2xl font-bold text-dark mb-4">{metodo.title}</h3>

                    <p className="text-gray-700 leading-relaxed">{metodo.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-4">Nuestros valores</h2>
              <div className="w-16 h-1 bg-primary mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {valores.map((valor, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-primary/10 rounded-full">
                    <i className={`${valor.icon} text-4xl text-primary`}></i>
                  </div>

                  <h3 className="text-xl font-bold text-dark mb-3">{valor.title}</h3>

                  <p className="text-gray-600 leading-relaxed">{valor.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-dark text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center">
                Colaboraciones y confianza
              </h2>

              <div className="space-y-6 text-lg leading-relaxed text-white/90">
                <p>
                  En JUSTCLEAN colaboramos estrechamente con <strong>inmobiliarias, administradores de fincas, servicios sociales y comunidades de vecinos</strong> en Mollet del Vallès, La Llagosta, Montornès del Vallès, Parets del Vallès y toda la comarca del Vallès Oriental.
                </p>

                <p>
                  Nuestra experiencia en casos de <strong>síndrome de Diógenes</strong> nos ha convertido en el socio de confianza de trabajadores sociales y familias que necesitan un servicio discreto, profesional y humano. Entendemos la complejidad emocional de estas situaciones y actuamos siempre con el máximo respeto y sensibilidad.
                </p>

                <p>
                  Trabajamos con las principales gestorías de residuos de la zona, garantizando una <strong>gestión responsable y legal</strong> de todos los enseres retirados. Donamos muebles y objetos en buen estado a organizaciones benéficas locales y reciclamos todo lo posible.
                </p>

                <p>
                  Nuestros clientes nos recomiendan por nuestra <strong>puntualidad, eficiencia y trato cercano</strong>. Más del 80% de nuestros nuevos clientes llegan por recomendación directa de clientes satisfechos en Mollet del Vallès y alrededores.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">+10</div>
                  <div className="text-sm text-white/70">Años de Experiencia</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">+500</div>
                  <div className="text-sm text-white/70">Pisos Vaciados</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">+200</div>
                  <div className="text-sm text-white/70">Casos Diógenes</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">24h</div>
                  <div className="text-sm text-white/70">Servicio Urgente</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-r from-primary to-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Quieres conocer más sobre nuestra forma de trabajar?
            </h2>

            <p className="text-white/90 text-lg mb-8">
              Contacta con nosotros sin compromiso y te explicaremos cómo podemos ayudarte
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:+34602365996"
                className="inline-flex items-center justify-center space-x-2 bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xl"></i>
                <span>Llamar Ahora</span>
              </a>

              <Link
                to="/contacto"
                className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Contactar</span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
      <CookieBanner />
    </div>
  );
};

export default EmpresaPage;